/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { min, max, sum } from 'lodash';

import { ApiSurveyDataCreate, ApiSurveyDataPreview } from '@/pages/survey_builder/types';

import { State, Question, QuestionOption, QuestionCondition } from './state';
import { RootState } from '@/state/types';

export const isNumeric = (num: string): boolean => {
  return /^-?(\d+)(|\.\d+)$/.test(`${num}`);
};

export const findQuestion = (state: State, questionId: number) =>
  state.questions.find(question => question.id === questionId);

export const findEditQuestion = (state: State) =>
  state.questions.find(question => question.id === state.activeQuestionId);

export const isQuestionTypeAdditionalText = (question: Question | undefined) =>
  question != null && question.type === 'additional_text';

export const additionalTypeQuestionHasText = (question: Question | undefined) =>
  isQuestionTypeAdditionalText(question) && !isQuestionTitleEmpty(question);

export const questionHasWeight = (question: Question) => !isQuestionTypeAdditionalText(question);

export const questionHasNumber = (question: Question) => !isQuestionTypeAdditionalText(question);

export const questionHasRequired = (question: Question) => !isQuestionTypeAdditionalText(question);

export const findOption = (question: Question, optionId: number) =>
  question.options.find(option => option.id === optionId);

export const questionHasOptions = (question: Question) => question.type === 'radio' || question.type === 'checkboxes';

export const questionHasConditions = (question: Question | undefined) =>
  question != null && question.conditions.length > 0;

export const isQuestionValidForConditions = (question: Question) =>
  questionHasOptions(question) && question.options.length > 0;

export const questionsForConditions = (questions: Question[]) => questions.filter(isQuestionValidForConditions);

export const findCondition = (question: Question, conditionId: number) =>
  question.conditions.find(condition => condition.id === conditionId);

export const isQuestionOptionTitleValid = (option: QuestionOption) => option.title.length > 0;

export const isQuestionOptionMultiplierValid = (option: QuestionOption, isWeighted: boolean) => {
  if (!isWeighted) {
    return true;
  }
  return option.multiplier >= 0;
};

export const isQuestionOptionValid = (option: QuestionOption, isWeighted: boolean) => {
  if (!isQuestionOptionTitleValid(option)) {
    return false;
  }
  if (!isQuestionOptionMultiplierValid(option, isWeighted)) {
    return false;
  }
  return true;
};

export const getQuestionsMap = (questions: Question[]): Map<number, Question> =>
  new Map(questions.map(question => [question.id, question] as [number, Question]));

export const isQuestionConditionQuestionValid = (
  questionsMap: Map<number, Question>,
  question: Question,
  condition: QuestionCondition
) => {
  if (condition.shownByQuestionId == null) {
    return false;
  }
  if (condition.shownByQuestionId === question.id) {
    return false;
  }
  const conditionQuestion = questionsMap.get(condition.shownByQuestionId);
  if (conditionQuestion == null) {
    return false;
  }
  if (!isQuestionValidForConditions(conditionQuestion)) {
    return false;
  }
  return true;
};

export const isQuestionConditionOptionValid = (questionsMap: Map<number, Question>, condition: QuestionCondition) => {
  if (condition.shownByQuestionId == null) {
    // if question is invalid we don't yet need to validate the option
    return true;
  }
  const conditionQuestion = questionsMap.get(condition.shownByQuestionId);
  if (conditionQuestion == null) {
    return true;
  }
  if (condition.shownByOptionId == null) {
    return false;
  }
  const conditionOption = conditionQuestion.options.find(option => option.id === condition.shownByOptionId);
  if (conditionOption == null) {
    return false;
  }
  return true;
};

export const isQuestionConditionValid = (
  questionsMap: Map<number, Question>,
  question: Question,
  condition: QuestionCondition
) => {
  if (!isQuestionConditionQuestionValid(questionsMap, question, condition)) {
    return false;
  }
  if (!isQuestionConditionOptionValid(questionsMap, condition)) {
    return false;
  }
  return true;
};

export const isQuestionTypeValid = (question: Question) => question.type != null;

export const isQuestionTitleValid = (question: Question) => question.title.length !== 0;

export const isQuestionTitleEmpty = (question: Question) => question.title === '';

export const isQuestionWeightValid = (question: Question, isWeighted: boolean) => {
  if (!isWeighted || !questionHasWeight(question)) {
    return true;
  }
  return question.weight >= 0;
};

export const isQuestionOptionsValid = (question: Question, isWeighted: boolean) => {
  if (!questionHasOptions(question)) {
    return true;
  }

  if (question.options.length === 0) {
    return false;
  }

  const invalidOptions = question.options.filter(option => !isQuestionOptionValid(option, isWeighted));

  if (invalidOptions.length > 0) {
    return false;
  }

  return true;
};

export const isQuestionConditionsValid = (questionsMap: Map<number, Question>, question: Question) => {
  if (!questionHasConditions(question)) {
    return true;
  }

  const invalidConditions = question.conditions.filter(
    condition => !isQuestionConditionValid(questionsMap, question, condition)
  );

  if (invalidConditions.length > 0) {
    return false;
  }

  return true;
};

export const isQuestionValid = (questionsMap: Map<number, Question>, question: Question, isWeighted: boolean) => {
  if (!isQuestionTypeValid(question)) {
    return false;
  }
  if (!isQuestionTitleValid(question)) {
    return false;
  }
  if (!isQuestionWeightValid(question, isWeighted)) {
    return false;
  }
  if (!isQuestionOptionsValid(question, isWeighted)) {
    return false;
  }
  if (!isQuestionConditionsValid(questionsMap, question)) {
    return false;
  }
  return true;
};

export const isSurveyTitleValid = (title: string) => title.length > 0;

export const isRiskThresholdValid = (threshold: string) => isNumeric(threshold) && parseFloat(threshold) >= 0;

export const isMidRiskThresholdValid = (midRiskThreshold: string, highRiskThreshold: string) => {
  if (!isRiskThresholdValid(midRiskThreshold)) {
    return false;
  }
  if (isRiskThresholdValid(highRiskThreshold) && parseFloat(highRiskThreshold) <= parseFloat(midRiskThreshold)) {
    return false;
  }
  return true;
};

export const isHighRiskThresholdValid = (highRiskThreshold: string, midRiskThreshold: string) => {
  if (!isRiskThresholdValid(highRiskThreshold)) {
    return false;
  }
  if (isRiskThresholdValid(midRiskThreshold) && parseFloat(highRiskThreshold) <= parseFloat(midRiskThreshold)) {
    return false;
  }
  return true;
};

export const hasRiskThresholds = (state: State) => {
  return state.isWeighted;
};

export const isSurveyValid = (state: State) => {
  if (!isSurveyTitleValid(state.title)) {
    return false;
  }
  if (hasRiskThresholds(state) && !isMidRiskThresholdValid(state.midRiskThreshold, state.highRiskThreshold)) {
    return false;
  }
  if (hasRiskThresholds(state) && !isHighRiskThresholdValid(state.highRiskThreshold, state.midRiskThreshold)) {
    return false;
  }
  const invalidQuestions = state.questions.filter(question => !question.isValid);
  if (invalidQuestions.length > 0) {
    return false;
  }
  return true;
};

export const getMinQuestionScore = (question: Question): number => {
  let score = 0;

  if (!questionHasWeight(question)) {
    return score;
  }

  if (questionHasOptions(question)) {
    if (question.options.length > 0) {
      score += min(question.options.map(option => question.weight * option.multiplier))!;
    }
  } else if (question.required) {
    score += question.weight;
  }

  return score;
};

export const getMaxQuestionScore = (question: Question): number => {
  let score = 0;

  if (!questionHasWeight(question)) {
    return score;
  }

  if (questionHasOptions(question)) {
    if (question.options.length > 0) {
      if (question.type === 'radio') {
        score += max(question.options.map(option => question.weight * option.multiplier))!;
      } else if (question.type === 'checkboxes') {
        score += sum(question.options.map(option => question.weight * option.multiplier))!;
      }
    }
  } else {
    score += question.weight;
  }

  return score;
};

export const getWeightedQuestions = (state: State): Question[] => {
  if (!state.isWeighted) {
    return [];
  }

  return state.questions.filter(questionHasWeight);
};

export const getMinSurveyScore = (state: State): number => {
  const weightedQuestions = getWeightedQuestions(state);

  if (weightedQuestions.length === 0) {
    return 0;
  }

  return sum(weightedQuestions.map(getMinQuestionScore))!;
};

export const getMaxSurveyScore = (state: State): number => {
  const weightedQuestions = getWeightedQuestions(state);

  if (weightedQuestions.length === 0) {
    return 0;
  }

  return sum(weightedQuestions.map(getMaxQuestionScore))!;
};

export const getSurveyData = (state: State, rootState: RootState): ApiSurveyDataCreate => {
  const mappedSurvey = {
    title: state.title!,
    is_weighted: state.isWeighted,
    mid_risk_threshold: parseFloat(state.midRiskThreshold),
    high_risk_threshold: parseFloat(state.highRiskThreshold),
    questions: state.questions.map((question, questionIdx) => {
      const addMappings = rootState.featureFlags ? rootState.featureFlags.vendor_risk_phase_1 : false;
      return mapQuestion(question, questionIdx, addMappings);
    }),
  };

  return mappedSurvey;
};

export const getSurveyDataPreview = (state: State, rootState: RootState): ApiSurveyDataPreview => {
  const data = getSurveyData(state, rootState);

  return {
    ...data,
    id: 123,
    questions: data.questions.map(question => ({
      ...question,
      text: '',
      options: question.options.map(option => ({
        ...option,
        picked: false,
      })),
    })),
  };
};

export const removeHtmlTags = (rawString: string): string => {
  return rawString.replace(/(<([^>]+)>)/gi, '');
};

function mapQuestion(question, questionIdx, addMappings = false) {
  const mappedQuestion = {
    id: question.id,
    title: question.title,
    description: question.description,
    page_number: question.pageNumber,
    type: question.type!,
    required: questionHasRequired(question) ? question.required : false,
    rank: questionIdx,
    weight: questionHasWeight(question) ? question.weight : 1,
    options: question.options.map(option => ({
      id: option.id,
      title: option.title,
      multiplier: option.multiplier,
    })),
    conditions: question.conditions.map(condition => ({
      shown_by_question_id: condition.shownByQuestionId,
      shown_by_option_id: condition.shownByOptionId,
    })),
  };

  if (addMappings) {
    mappedQuestion.mappings = question.mappings.map(object => {
      return {
        object_id: object.id,
        object_type: object.type,
        title: object.title,
      };
    });
  }

  return mappedQuestion;
}
